import React, { useState } from "react";
import Navbar from "@/components/Navbar";
import InterfaceMockup from "@/components/InterfaceMockup";
import MockupScenes from "@/components/MockupScenes";
import { useI18n } from "@/lib/i18n";
import { useLandingExtras } from "@/locales/landingExtras";
import { useWorkspaceTranslation } from "@/locales/workspaceExtras";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { trackMarketing } from "@/lib/marketing";
import {
  ArrowRight, ArrowUpRight, FileText, Landmark, Anchor, Building2,
  MousePointerClick, UploadCloud, MessageSquareQuote, Layers, Lock, ShieldCheck,
  Check, Sparkles, CheckCircle2, Send, ChevronDown, Plus, Minus,
  CirclePlay,
} from "lucide-react";

/* =============================================================================
   Small helpers
   ============================================================================= */

function SectionHeading({ label, title, sub, id, align = "left" }) {
  return (
    <div id={id} className={`mb-10 sm:mb-14 max-w-3xl min-w-0 ${align === "center" ? "mx-auto text-center" : ""}`}>
      {label && <div className="kicker mb-4">— {label}</div>}
      <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl lg:text-[3.4rem] leading-[1.05] tracking-tight break-words">
        {title}
      </h2>
      {sub && <p className="mt-5 text-[var(--td-text-2)] text-lg leading-relaxed">{sub}</p>}
    </div>
  );
}

function Card({ children, className = "", testid }) {
  return (
    <div data-testid={testid} className={`card-surface p-5 sm:p-7 ${className}`}>
      {children}
    </div>
  );
}

/* =============================================================================
   Sections
   ============================================================================= */

function Hero({ t, lang }) {
  return (
    <section id="top" className="relative pt-24 sm:pt-28 md:pt-40 pb-14 sm:pb-16 md:pb-24 overflow-hidden">
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-24 -left-32 w-[560px] h-[560px] rounded-full bg-[var(--td-gold-muted)] blur-[140px] opacity-40" />
        <div className="absolute bottom-0 right-0 w-[420px] h-[420px] rounded-full bg-blue-500/10 blur-[140px] opacity-40" />
      </div>

      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 md:px-10 relative z-10">
        <div className="grid lg:grid-cols-12 gap-8 sm:gap-10 items-center min-w-0">
          <div className="lg:col-span-6 fade-up min-w-0">
            <div className="kicker mb-5 sm:mb-6">— {t("hero_label")}</div>
            <h1 className="font-serif text-[2.15rem] min-[390px]:text-[2.35rem] sm:text-[3.4rem] md:text-[4.2rem] lg:text-[4.4rem] leading-[1.04] tracking-tight text-[var(--td-text)] mb-5 sm:mb-6 break-words">
              {t("hero_title").split("—")[0].trim()}{t("hero_title").includes("—") && <span className="text-[var(--td-gold)]"> — </span>}
              <span className="italic text-[var(--td-text-2)]">{t("hero_title").split("—")[1]?.trim()}</span>
            </h1>
            <p data-testid="hero-deal-line" className="font-serif text-xl sm:text-2xl leading-snug text-[var(--td-gold)] mb-4 break-words">
              {t("hero_deal_line")}
            </p>
            <p className="max-w-2xl text-base md:text-lg text-[var(--td-text-2)] leading-relaxed mb-9">
              {t("hero_sub")}
            </p>
            <div className="flex flex-col sm:flex-row sm:flex-wrap gap-3">
              <a href="/signup" onClick={() => trackMarketing("signup_cta", { page: "home", language: lang })} data-testid="hero-cta-primary" className="btn-gold inline-flex items-center justify-center gap-2 w-full sm:w-auto">
                {t("hero_cta_primary")} <ArrowRight size={16} />
              </a>
              <a href="/demo" onClick={() => trackMarketing("demo_cta", { page: "home", language: lang })} className="btn-ghost inline-flex items-center justify-center gap-2 w-full sm:w-auto !border-[var(--td-gold)] !text-[var(--td-gold)] ring-1 ring-[var(--td-gold)]/35 ring-offset-2 ring-offset-[var(--td-bg)]">
                <CirclePlay size={18} /> {t("nav_demo")}
              </a>
              <a href="#product" data-testid="hero-cta-secondary" className="btn-ghost inline-flex items-center justify-center gap-2 w-full sm:w-auto">
                {t("hero_cta_secondary")}
              </a>
              <a href="/signin" data-testid="hero-cta-login" className="text-sm text-center text-[var(--td-text-2)] hover:text-[var(--td-gold)] transition-colors self-center px-2 py-2 w-full sm:w-auto">
                {t("hero_cta_login")}
              </a>
            </div>

            <div className="mt-10 flex flex-wrap gap-x-6 gap-y-2 text-xs font-mono text-[var(--td-text-3)] uppercase tracking-widest">
              {[1, 2, 3, 4, 5].map((n) => (
                <div key={n} className="flex items-center gap-1.5">
                  <Check size={11} className="text-[var(--td-gold)]" />
                  {t(`trust_${n}`)}
                </div>
              ))}
            </div>
          </div>

          <div className="lg:col-span-6 fade-up fade-up-2 min-w-0 w-full">
            <div className="lg:scale-110 lg:origin-left">
              <InterfaceMockup caption={t("hero_mockup_caption")} />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function ProductIntro({ t }) {
  const cards = [
    { icon: Layers, k: "1" },
    { icon: FileText, k: "2" },
    { icon: ShieldCheck, k: "3" },
    { icon: Building2, k: "4" },
  ];
  return (
    <section id="product" className="py-24 md:py-32 hairline-top">
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 md:px-10">
        <SectionHeading label={t("product_label")} title={t("product_title")} sub={t("product_sub")} />
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
          {cards.map(({ icon: Icon, k }) => (
            <Card key={k} testid={`product-card-${k}`}>
              <Icon size={22} className="text-[var(--td-gold)] mb-5" />
              <div className="font-serif text-xl mb-2">{t(`product_card_${k}_title`)}</div>
              <p className="text-sm text-[var(--td-text-2)] leading-relaxed">{t(`product_card_${k}_desc`)}</p>
            </Card>
          ))}
        </div>
      </div>
    </section>
  );
}

function ThreeWays({ t }) {
  const ways = [
    { k: "1", Icon: MousePointerClick, featured: false },
    { k: "2", Icon: UploadCloud, featured: false },
    { k: "3", Icon: MessageSquareQuote, featured: true },
  ];
  return (
    <section className="py-24 md:py-32 hairline-top">
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 md:px-10">
        <SectionHeading title={t("three_ways_title")} />
        <div className="grid md:grid-cols-3 gap-4">
          {ways.map(({ k, Icon, featured }) => (
            <div
              key={k}
              className={`card-surface p-5 sm:p-8 flex flex-col relative ${featured ? "border-[var(--td-gold)]" : ""}`}
              data-testid={`three-ways-${k}`}
            >
              {featured && (
                <div className="absolute -top-3 left-6 bg-[var(--td-gold)] text-[var(--td-bg)] px-3 py-0.5 font-mono text-[10px] uppercase tracking-widest">
                  {t("three_ways_3_badge")}
                </div>
              )}
              <Icon size={26} className={`mb-5 ${featured ? "text-[var(--td-gold)]" : "text-[var(--td-text-2)]"}`} />
              <div className="font-serif text-2xl mb-2">{t(`three_ways_${k}_title`)}</div>
              <p className="text-sm text-[var(--td-text-2)] leading-relaxed flex-1 mb-6">{t(`three_ways_${k}_desc`)}</p>
              <a href="/signup" className="text-xs font-mono uppercase tracking-widest text-[var(--td-gold)] hover:text-[var(--td-gold-hover)] inline-flex items-center gap-1.5">
                {t(`three_ways_${k}_cta`)} <ArrowRight size={12} />
              </a>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function DocumentCategories({ t }) {
  const x = useLandingExtras();
  const { tr } = useWorkspaceTranslation();
  const cats = [
    { key: "commercial", Icon: FileText,
      route: "/templates/letter-of-intent", samples: [tr("Proforma Invoice"), tr("Commercial Invoice"), tr("Packing List"), tr("Sales and Purchase Agreement (SPA)"), x("sampleLoi"), x("sampleIcpo"), x("sampleOffer"), x("sampleFco"), x("sampleDd"), x("sampleFollow")] },
    { key: "trade_finance", Icon: Landmark,
      route: "/templates/letter-of-credit", samples: [x("sampleLc"), x("sampleSblc"), x("sampleBcl"), x("sampleChecklist"), x("sampleFlags")] },
    { key: "shipping", Icon: Anchor,
      route: "/templates/shipping-correspondence", samples: [x("sampleRecap"), x("sampleLaytime"), x("sampleOwner"), x("sampleClaim")] },
    { key: "management", Icon: Building2,
      route: "/templates/fco", samples: [x("sampleSummary"), x("sampleNext")] },
  ];
  return (
    <section id="documents" className="py-24 md:py-32 hairline-top">
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 md:px-10">
        <SectionHeading title={t("cats_title")} />
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {cats.map(({ key, Icon, samples, route }) => (
            <Card key={key} testid={`cat-${key}`} className="flex flex-col">
              <div className="flex items-start justify-between mb-5">
                <Icon size={24} className="text-[var(--td-gold)]" />
                <span className="font-mono text-[10px] uppercase tracking-widest text-[var(--td-text-3)]">
                  {samples.length} {x("templates")}
                </span>
              </div>
              <div className="font-serif text-2xl mb-2">{t(`cat_${key}`)}</div>
              <p className="text-sm text-[var(--td-text-2)] leading-relaxed mb-5">{t(`cat_${key}_desc`)}</p>
              <ul className="space-y-1.5 mb-6 flex-1">
                {samples.slice(0, 4).map((s) => (
                  <li key={s} className="text-xs font-mono text-[var(--td-text-2)] flex items-center gap-2">
                    <span className="w-1 h-1 bg-[var(--td-gold)] rounded-full" /> {s}
                  </li>
                ))}
              </ul>
              <a href={route} className="text-xs font-mono uppercase tracking-widest text-[var(--td-gold)] hover:text-[var(--td-gold-hover)] inline-flex items-center gap-1.5">
                {t("cats_view")} <ArrowRight size={12} />
              </a>
            </Card>
          ))}
        </div>
      </div>
    </section>
  );
}

function PlatformPreview({ t }) {
  return (
    <section className="py-24 md:py-32 hairline-top">
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 md:px-10">
        <SectionHeading label={t("preview_label")} title={t("preview_title")} align="center" />
        <div className="max-w-5xl mx-auto">
          <MockupScenes />
        </div>
        <div className="mt-12 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 max-w-5xl mx-auto">
          {[1, 2, 3, 4, 5, 6].map((n) => (
            <div key={n} className="flex items-start gap-2">
              <div className="font-mono text-[10px] text-[var(--td-gold)] tracking-widest pt-0.5">0{n}</div>
              <div className="text-sm text-[var(--td-text-2)] leading-tight">{t(`preview_step_${n}`)}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function HowItWorks({ t }) {
  return (
    <section id="how" className="py-24 md:py-32 hairline-top">
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 md:px-10">
        <SectionHeading title={t("how_title")} />
        <div className="relative">
          <div className="hidden md:block absolute top-8 left-8 right-8 h-px bg-gradient-to-r from-[var(--td-border)] via-[var(--td-gold)] to-[var(--td-border)] opacity-40" />
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4 relative">
            {[1, 2, 3, 4, 5].map((n) => (
              <div key={n} className="flex md:block gap-4">
                <div className="w-16 h-16 shrink-0 rounded-full border-2 border-[var(--td-gold)] bg-[var(--td-bg)] flex items-center justify-center font-mono text-sm text-[var(--td-gold)] tracking-widest md:mb-4 relative z-10">
                  0{n}
                </div>
                <div className="pt-1 md:pt-0">
                  <div className="font-serif text-xl mb-1">{t(`how_${n}_t`)}</div>
                  <p className="text-sm text-[var(--td-text-2)] leading-relaxed">{t(`how_${n}_d`)}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

function UseCases({ t }) {
  const x = useLandingExtras();
  return (
    <section id="use-cases" className="py-24 md:py-32 hairline-top">
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 md:px-10">
        <SectionHeading title={t("uc_title")} />
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[1, 2, 3, 4, 5, 6].map((n) => (
            <Card key={n} testid={`use-case-${n}`} className="flex flex-col">
              <div className="font-mono text-[10px] uppercase tracking-widest text-[var(--td-gold)] mb-3">
                {x("case")} 0{n}
              </div>
              <div className="font-serif text-2xl mb-3">{t(`uc_${n}_t`)}</div>
              <p className="text-sm text-[var(--td-text-2)] leading-relaxed">{t(`uc_${n}_d`)}</p>
            </Card>
          ))}
        </div>
      </div>
    </section>
  );
}

function AIDrafting({ t }) {
  return (
    <section className="py-24 md:py-32 hairline-top relative overflow-hidden">
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[700px] h-[400px] rounded-full bg-[var(--td-gold-muted)] blur-[160px] opacity-30" />
      </div>
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 md:px-10 relative z-10 grid lg:grid-cols-12 gap-12 min-w-0">
        <div className="lg:col-span-6">
          <Sparkles size={30} className="text-[var(--td-gold)] mb-6" />
          <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl leading-tight mb-6 break-words">{t("ai_title")}</h2>
          <p className="text-[var(--td-text-2)] text-lg leading-relaxed">{t("ai_sub")}</p>
        </div>
        <div className="lg:col-span-6 lg:pl-8 lg:border-l lg:border-[var(--td-border)]">
          <ul className="space-y-4">
            {[1, 2, 3, 4, 5, 6, 7].map((n) => (
              <li key={n} className="flex items-start gap-3">
                <Check size={14} className="text-[var(--td-gold)] mt-1 shrink-0" />
                <span className="text-[var(--td-text)]">{t(`ai_b_${n}`)}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </section>
  );
}

function ProfessionalSupport({ t }) {
  return (
    <section className="py-20 hairline-top">
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 md:px-10">
        <div className="card-surface p-5 sm:p-8 md:p-14 flex flex-col md:flex-row items-start md:items-center gap-8 justify-between">
          <div className="max-w-2xl">
            <h3 className="font-serif text-3xl md:text-4xl leading-tight mb-3">{t("ps_title")}</h3>
            <p className="text-[var(--td-text-2)] leading-relaxed">{t("ps_sub")}</p>
          </div>
          <a href="#request" className="btn-gold inline-flex items-center justify-center gap-2 shrink-0 w-full md:w-auto" data-testid="ps-cta">
            {t("ps_cta")} <ArrowRight size={16} />
          </a>
        </div>
      </div>
    </section>
  );
}

function Confidentiality({ t }) {
  return (
    <section id="about" className="py-24 md:py-32 hairline-top">
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 md:px-10 grid lg:grid-cols-12 gap-10 min-w-0">
        <div className="lg:col-span-5">
          <Lock size={30} className="text-[var(--td-gold)] mb-6" />
          <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl leading-tight break-words">{t("conf_title")}</h2>
        </div>
        <div className="lg:col-span-7">
          <div className="grid sm:grid-cols-2 gap-x-6 gap-y-4">
            {[1, 2, 3, 4, 5, 6, 7, 8].map((n) => (
              <div key={n} className="flex items-start gap-3">
                <ShieldCheck size={14} className="text-[var(--td-gold)] mt-1 shrink-0" />
                <span className="text-sm text-[var(--td-text-2)]">{t(`conf_${n}`)}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

function Membership({ t }) {
  const x = useLandingExtras();
  const areas = Array.from({ length: 9 }, (_, index) => t(`mem_area_${index + 1}`));
  return (
    <section className="py-24 md:py-32 hairline-top">
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 md:px-10">
        <div className="card-surface p-5 sm:p-8 md:p-14 relative overflow-hidden">
          <div className="absolute -top-20 -right-20 w-[320px] h-[320px] rounded-full bg-[var(--td-gold-muted)] blur-[100px] opacity-40 pointer-events-none" />
          <div className="relative z-10 grid lg:grid-cols-2 gap-12">
            <div>
              <div className="kicker mb-4">— {t("mem_label")}</div>
              <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl leading-tight mb-6 break-words">{t("mem_title")}</h2>
              <p className="text-[var(--td-text-2)] text-lg leading-relaxed mb-8">{t("mem_sub")}</p>
              <a href="/signup" className="btn-gold inline-flex items-center gap-2" data-testid="mem-cta">
                {t("hero_cta_primary")} <ArrowRight size={16} />
              </a>
              <div className="mt-6 space-y-2">
                <div className="text-xs font-mono text-[var(--td-text-3)] italic">{t("mem_note")}</div>
                <div className="text-xs font-mono text-[var(--td-text-3)] italic">{t("mem_expert")}</div>
              </div>
            </div>
            <div>
              <div className="font-mono text-[10px] uppercase tracking-widest text-[var(--td-gold)] mb-5">
                {x("included")}
              </div>
              <ul className="grid sm:grid-cols-2 gap-3">
                {areas.map((a, i) => (
                  <li key={i} className="flex items-center gap-2 text-sm text-[var(--td-text-2)]">
                    <div className="w-1 h-1 bg-[var(--td-gold)] rounded-full" /> {a}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function EarlyAccessForm({ t }) {
  const x = useLandingExtras();
  return (
    <section id="early-access" className="py-24 md:py-32 hairline-top">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 md:px-10 text-center">
        <div className="kicker mb-4">— {t("hero_label")}</div>
        <h2 className="font-serif text-3xl sm:text-5xl md:text-6xl leading-tight mb-6">{t("ea_title")}</h2>
        <p className="text-[var(--td-text-2)] text-lg leading-relaxed max-w-3xl mx-auto mb-10">{t("ea_sub")}</p>
        <div className="grid sm:grid-cols-3 gap-4 text-left mb-10">
          <Card><FileText size={20} className="text-[var(--td-gold)] mb-4" /><div className="font-serif text-xl mb-2">{x("free1t")}</div><p className="text-sm text-[var(--td-text-2)]">{x("free1d")}</p></Card>
          <Card><Sparkles size={20} className="text-[var(--td-gold)] mb-4" /><div className="font-serif text-xl mb-2">{x("free2t")}</div><p className="text-sm text-[var(--td-text-2)]">{x("free2d")}</p></Card>
          <Card><ShieldCheck size={20} className="text-[var(--td-gold)] mb-4" /><div className="font-serif text-xl mb-2">{x("free3t")}</div><p className="text-sm text-[var(--td-text-2)]">{x("free3d")}</p></Card>
        </div>
        <div className="flex flex-col sm:flex-row justify-center gap-3">
          <a href="/signup" className="btn-gold inline-flex items-center justify-center gap-2" data-testid="start-free-signup">{t("ea_cta")} <ArrowRight size={15} /></a>
          <a href="/pricing" className="btn-ghost inline-flex items-center justify-center gap-2">{t("nav_pricing")} <ArrowUpRight size={14} /></a>
        </div>
        <p className="mt-5 text-[10px] font-mono uppercase tracking-widest text-[var(--td-text-3)]">{x("freeNote")}</p>
      </div>
    </section>
  );
}

const WORK_AREAS = [
  "International Trade", "Commodity Trading", "Shipping", "Chartering",
  "Trade Finance", "Import and Export", "Corporate Services", "Other",
];

function LegacyEarlyAccessForm({ t }) {
  const [form, setForm] = useState({
    first_name: "", last_name: "", company: "", business_email: "",
    country: "", work_area: WORK_AREAS[0], preferred_language: "en",
    main_document_requirement: "", message: "",
  });
  const [status, setStatus] = useState("idle");
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setStatus("loading"); setErr("");
    try {
      await api.submitEarlyAccess(form);
      setStatus("ok");
      toast.success(t("ea_ok"));
      setForm({ ...form, message: "", main_document_requirement: "" });
    } catch (e2) {
      setStatus("err");
      setErr(e2?.response?.data?.detail || t("ea_err"));
      toast.error(t("ea_err"));
    }
  };

  const input = "td-input";
  const label = "td-label";

  return (
    <section id="early-access" className="py-24 md:py-32 hairline-top">
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 md:px-10 grid lg:grid-cols-12 gap-12 min-w-0">
        <div className="lg:col-span-5">
          <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl leading-tight mb-5 break-words">{t("ea_title")}</h2>
          <p className="text-[var(--td-text-2)] text-lg leading-relaxed mb-8">{t("ea_sub")}</p>
          <div className="space-y-3 text-sm text-[var(--td-text-2)]">
            <div className="flex items-start gap-2"><CheckCircle2 size={14} className="text-[var(--td-gold)] mt-1" /> {t("trust_2")}</div>
            <div className="flex items-start gap-2"><CheckCircle2 size={14} className="text-[var(--td-gold)] mt-1" /> {t("trust_4")}</div>
            <div className="flex items-start gap-2"><CheckCircle2 size={14} className="text-[var(--td-gold)] mt-1" /> {t("trust_5")}</div>
          </div>
        </div>

        <form onSubmit={submit} className="lg:col-span-7 card-surface p-5 sm:p-8 md:p-10 space-y-5 min-w-0" data-testid="early-access-form">
          <div className="grid md:grid-cols-2 gap-5">
            <div>
              <label className={label}>{t("ea_first")} *</label>
              <input required data-testid="ea-first" className={input} value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} />
            </div>
            <div>
              <label className={label}>{t("ea_last")} *</label>
              <input required data-testid="ea-last" className={input} value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} />
            </div>
            <div>
              <label className={label}>{t("ea_company")} *</label>
              <input required data-testid="ea-company" className={input} value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} />
            </div>
            <div>
              <label className={label}>{t("ea_email")} *</label>
              <input required type="email" data-testid="ea-email" className={input} value={form.business_email} onChange={(e) => setForm({ ...form, business_email: e.target.value })} />
            </div>
            <div>
              <label className={label}>{t("ea_country")} *</label>
              <input required data-testid="ea-country" className={input} value={form.country} onChange={(e) => setForm({ ...form, country: e.target.value })} />
            </div>
            <div>
              <label className={label}>{t("ea_area")} *</label>
              <select data-testid="ea-area" className={input} value={form.work_area} onChange={(e) => setForm({ ...form, work_area: e.target.value })}>
                {WORK_AREAS.map((w, i) => <option key={w} value={w}>{t(`ea_area_opt_${i + 1}`)}</option>)}
              </select>
            </div>
            <div>
              <label className={label}>{t("ea_lang")}</label>
              <select data-testid="ea-lang" className={input} value={form.preferred_language} onChange={(e) => setForm({ ...form, preferred_language: e.target.value })}>
                <option value="en">English</option>
                <option value="it">Italiano</option>
                <option value="de">Deutsch</option>
              </select>
            </div>
            <div>
              <label className={label}>{t("ea_doc")}</label>
              <input data-testid="ea-doc" className={input} placeholder="e.g. LOI, LC draft, DD questionnaire" value={form.main_document_requirement} onChange={(e) => setForm({ ...form, main_document_requirement: e.target.value })} />
            </div>
          </div>
          <div>
            <label className={label}>{t("ea_message")}</label>
            <textarea rows={3} data-testid="ea-message" className={`${input} resize-none`} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} />
          </div>
          <div className="text-[10px] font-mono text-[var(--td-text-3)]">{t("ea_privacy")}</div>
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <button type="submit" disabled={status === "loading"} data-testid="ea-submit"
              className="btn-gold inline-flex items-center justify-center gap-2 disabled:opacity-50 w-full sm:w-auto">
              {status === "loading" ? "…" : <>{t("ea_cta")} <Send size={14} /></>}
            </button>
            {status === "ok" && (
              <div className="flex items-center gap-2 text-[var(--td-gold)] text-sm" data-testid="ea-success">
                <CheckCircle2 size={14} /> {t("ea_ok")}
              </div>
            )}
            {status === "err" && (
              <div className="text-sm text-red-400" data-testid="ea-error">{err}</div>
            )}
          </div>
        </form>
      </div>
    </section>
  );
}

const REQUEST_TYPES = ["Commercial document", "Trade finance", "Shipping / Chartering", "Corporate / DD", "Other"];

function RequestForm({ t }) {
  const x = useLandingExtras();
  const [form, setForm] = useState({
    company_name: "", contact_name: "", email: "", phone: "",
    deal_type: REQUEST_TYPES[0], brief: "", urgency: "normal",
  });
  const [status, setStatus] = useState("idle");

  const submit = async (e) => {
    e.preventDefault();
    setStatus("loading");
    try {
      await api.submitIntake(form);
      setStatus("ok");
      toast.success(t("ar_ok"));
      setForm({ ...form, brief: "", phone: "" });
    } catch {
      setStatus("err");
      toast.error(t("ea_err"));
    }
  };

  return (
    <section id="request" className="py-24 md:py-32 hairline-top">
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 md:px-10 grid lg:grid-cols-12 gap-10 min-w-0">
        <div className="lg:col-span-4">
          <h2 className="font-serif text-4xl leading-tight mb-4">{t("ar_title")}</h2>
          <p className="text-[var(--td-text-2)] leading-relaxed">{t("ar_sub")}</p>
        </div>
        <form onSubmit={submit} data-testid="request-form" className="lg:col-span-8 card-surface p-5 sm:p-8 md:p-10 space-y-5 min-w-0">
          <div className="grid md:grid-cols-2 gap-5">
            <div>
              <label className="td-label">{t("ar_name")} *</label>
              <input required data-testid="ar-name" className="td-input" value={form.contact_name} onChange={(e) => setForm({ ...form, contact_name: e.target.value })} />
            </div>
            <div>
              <label className="td-label">{t("ar_company")} *</label>
              <input required data-testid="ar-company" className="td-input" value={form.company_name} onChange={(e) => setForm({ ...form, company_name: e.target.value })} />
            </div>
            <div>
              <label className="td-label">{t("ar_email")} *</label>
              <input required type="email" data-testid="ar-email" className="td-input" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
            </div>
            <div>
              <label className="td-label">{t("ar_type")} *</label>
              <select data-testid="ar-type" className="td-input" value={form.deal_type} onChange={(e) => setForm({ ...form, deal_type: e.target.value })}>
                {REQUEST_TYPES.map((r, i) => <option key={r} value={r}>{t(`ar_type_opt_${i + 1}`)}</option>)}
              </select>
            </div>
            <div>
              <label className="td-label">{t("ar_urgency")}</label>
              <select data-testid="ar-urgency" className="td-input" value={form.urgency} onChange={(e) => setForm({ ...form, urgency: e.target.value })}>
                <option value="normal">{t("ar_urgency_1")}</option>
                <option value="high">{t("ar_urgency_2")}</option>
                <option value="urgent">{t("ar_urgency_3")}</option>
              </select>
            </div>
          </div>
          <div>
            <label className="td-label">{t("ar_desc")} *</label>
            <textarea rows={4} required data-testid="ar-desc" className="td-input resize-none" placeholder={t("ar_desc_ph")} value={form.brief} onChange={(e) => setForm({ ...form, brief: e.target.value })} />
          </div>
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div className="font-mono text-[10px] uppercase tracking-widest text-[var(--td-text-3)]">
              {x("confidential")}
            </div>
            <button type="submit" disabled={status === "loading"} data-testid="ar-submit"
              className="btn-gold inline-flex items-center justify-center gap-2 disabled:opacity-50 w-full sm:w-auto">
              {status === "loading" ? "…" : <>{t("ar_cta")} <Send size={14} /></>}
            </button>
          </div>
          {status === "ok" && (
            <div className="flex items-center gap-2 text-[var(--td-gold)] text-sm" data-testid="ar-success">
              <CheckCircle2 size={14} /> {t("ar_ok")}
            </div>
          )}
        </form>
      </div>
    </section>
  );
}

function FAQ({ t }) {
  const [open, setOpen] = useState(0);
  return (
    <section id="faq" className="py-24 md:py-32 hairline-top">
      <div className="max-w-[1000px] mx-auto px-4 sm:px-6 md:px-10">
        <SectionHeading title={t("faq_title")} align="center" />
        <div className="space-y-3">
          {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11].map((n) => {
            const isOpen = open === n;
            return (
              <div key={n} className={`card-surface transition-colors ${isOpen ? "border-[var(--td-gold)]" : ""}`}>
                <button
                  onClick={() => setOpen(isOpen ? 0 : n)}
                  data-testid={`faq-q-${n}`}
                  className="w-full text-left p-5 md:p-6 flex items-start justify-between gap-4 group"
                >
                  <span className="font-serif text-lg md:text-xl text-[var(--td-text)] group-hover:text-[var(--td-gold)] transition-colors">
                    {t(`faq_q_${n}`)}
                  </span>
                  <span className="mt-1 shrink-0 text-[var(--td-gold)]">
                    {isOpen ? <Minus size={16} /> : <Plus size={16} />}
                  </span>
                </button>
                {isOpen && (
                  <div className="px-5 md:px-6 pb-6 -mt-1 text-[var(--td-text-2)] leading-relaxed">
                    {t(`faq_a_${n}`)}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function Footer({ t }) {
  const x = useLandingExtras();
  const cols = [
    { title: t("ft_product"), links: [
      { l: t("ft_link_overview"), h: "#product" },
      { l: t("ft_link_pricing"), h: "/pricing", isRoute: true },
      { l: t("ft_link_templates"), h: "#documents" },
      { l: t("ft_link_how"), h: "#how" },
      { l: t("ft_link_use_cases"), h: "#use-cases" },
      { l: t("ft_link_early"), h: "/signup", isRoute: true },
      { l: t("ft_link_login"), h: "/signin", isRoute: true },
    ]},
    { title: t("ft_docs"), links: [
      { l: "Letter of Intent", h: "/templates/letter-of-intent", isRoute: true },
      { l: "ICPO", h: "/templates/icpo", isRoute: true },
      { l: "Full Corporate Offer", h: "/templates/fco", isRoute: true },
      { l: t("cat_trade_finance"), h: "/templates/letter-of-credit", isRoute: true },
      { l: t("cat_shipping"), h: "/templates/shipping-correspondence", isRoute: true },
    ]},
    { title: t("ft_company"), links: [
      { l: t("ft_link_about"), h: "#about" },
      { l: t("ft_link_contact"), h: "#contact" },
      { l: t("ft_link_support"), h: "#request" },
    ]},
    { title: t("ft_legal"), links: [
      { l: "Impressum", h: "/legal/impressum", isRoute: true },
      { l: t("ft_link_privacy"), h: "/privacy-policy", isRoute: true },
      { l: t("ft_link_terms"), h: "/legal/terms", isRoute: true },
      { l: t("ft_link_subscription"), h: "/legal/subscription", isRoute: true },
      { l: t("ft_link_cookies"), h: "/legal/cookies", isRoute: true },
      { l: t("ft_link_dp"), h: "/legal/data-processing", isRoute: true },
      { l: t("ft_link_nda"), h: "/legal/nda", isRoute: true },
    ]},
  ];
  return (
    <footer id="contact" className="hairline-top pt-20 pb-10">
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 md:px-10">
        <div className="grid md:grid-cols-2 lg:grid-cols-6 gap-10 md:gap-8 mb-16">
          <div className="lg:col-span-2">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-3 h-3 bg-[var(--td-gold)] rotate-45" />
              <span className="font-serif text-xl">TradeDocs<span className="text-[var(--td-gold)]"> · </span>Desk</span>
            </div>
            <p className="text-sm text-[var(--td-text-2)] leading-relaxed max-w-xs mb-5">
              {t("ft_tagline")}
            </p>
            <a href="mailto:desk@tradedocs.online" data-testid="footer-contact"
               className="text-sm text-[var(--td-text)] hover:text-[var(--td-gold)] transition-colors">
              desk@tradedocs.online
            </a>
          </div>
          {cols.map((c, i) => (
            <div key={i}>
              <div className="kicker mb-4">{c.title}</div>
              <ul className="space-y-2">
                {c.links.map((lk, j) => (
                  <li key={j}>
                    {lk.isRoute ? (
                      <a href={lk.h} className="text-sm text-[var(--td-text-2)] hover:text-[var(--td-gold)] transition-colors">{lk.l}</a>
                    ) : (
                      <a href={lk.h} className="text-sm text-[var(--td-text-2)] hover:text-[var(--td-gold)] transition-colors">{lk.l}</a>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
        <div className="pt-8 mb-8 hairline-top text-xs font-mono text-[var(--td-text-3)] space-y-1 max-w-md">
          <div className="text-[var(--td-text-2)] uppercase tracking-widest text-[10px] mb-2">{x("legalEntity")}</div>
          <div className="text-[var(--td-text-2)]">AIT Technologies GmbH</div>
          <div>Im Technologiepark 1</div>
          <div>15236 Frankfurt (Oder), Ortsteil Siedlung Markendorf</div>
          <div>{x("germany")}</div>
          <div className="pt-2">{x("register")}: HRB 14115 FF</div>
          <div>{x("vat")}: DE286 799 230</div>
          <div>{x("tax")}: 061 / 105 / 01666</div>
          <div className="pt-2 text-[var(--td-gold)]">desk@tradedocs.online</div>
        </div>
        <div className="pt-8 hairline-top flex flex-col md:flex-row items-start md:items-center justify-between gap-4 text-xs font-mono text-[var(--td-text-3)]">
          <div>© 2026 AIT Technologies GmbH · TradeDocs AI Desk · {t("ft_rights")}</div>
          <div className="flex items-center gap-4">
            <span>{t("ft_languages")}: EN · IT · DE · ES · ZH · RU</span>
          </div>
        </div>
      </div>
    </footer>
  );
}

/* =============================================================================
   Landing shell
   ============================================================================= */

export default function Landing() {
  const { t, lang } = useI18n();
  return (
    <div className="landing-page min-h-[100dvh] relative overflow-x-clip">
      <Navbar />
      <Hero t={t} lang={lang} />
      <ProductIntro t={t} />
      <ThreeWays t={t} />
      <DocumentCategories t={t} />
      <PlatformPreview t={t} />
      <HowItWorks t={t} />
      <UseCases t={t} />
      <AIDrafting t={t} />
      <ProfessionalSupport t={t} />
      <Confidentiality t={t} />
      <Membership t={t} />
      <EarlyAccessForm t={t} />
      <RequestForm t={t} />
      <FAQ t={t} />
      <Footer t={t} />
    </div>
  );
}
